// ABOUTME: The attempts of a job as a selectable list: number, state, start time and duration on each
// ABOUTME: row; the selected row is highlighted and opens up to show the attempt's full facts.

import { alpha, Box, List, ListItem, useTheme } from '@ds/coolshop';
import { format } from 'date-fns';
import { Fragment, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

import { JobAttempt } from '../../../types';
import { formatDuration } from '../../../utils/duration';
import { JobStateLabel } from '../../JobStateLabel';
import { AttemptSummary } from './AttemptSummary';

/** "08:26:10 · 2m 14s": when it started and how long it ran (or has been running). */
const startAndDuration = (attempt: JobAttempt) =>
    attempt.startedAt
        ? `${format(new Date(attempt.startedAt), 'HH:mm:ss')} · ${formatDuration(
              attempt.startedAt,
              attempt.endedAt,
          )}`
        : '';

interface IAttemptListProps {
    attempts: JobAttempt[];
    selected?: number;
    onSelect: (n: number) => void;
}

export function AttemptList(props: Readonly<IAttemptListProps>) {
    const { attempts, selected, onSelect } = props;
    const intl = useIntl();
    const theme = useTheme();
    // The tint of a selected list row, so the opened facts read as part of their row.
    const selectedTint = alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity);
    const scroller = useRef<HTMLDivElement>(null);
    const selectedRow = useRef<HTMLLIElement>(null);
    const selectedFacts = useRef<HTMLDivElement>(null);

    // Bring the selected attempt (e.g. a retry's new one) into view by scrolling the list alone,
    // never the side column around it: its facts if they fit, its row first in any case.
    useEffect(() => {
        const list = scroller.current;
        const row = selectedRow.current;
        if (!list || !row) return;
        // Offsets are relative to the list box, which is positioned.
        const top = row.offsetTop;
        const facts = selectedFacts.current;
        const bottom = facts ? facts.offsetTop + facts.offsetHeight : top + row.offsetHeight;
        if (top < list.scrollTop) {
            list.scrollTop = top;
        } else if (bottom > list.scrollTop + list.clientHeight) {
            list.scrollTop = Math.min(top, bottom - list.clientHeight);
        }
    }, [selected, attempts.length]);

    return (
        <Box
            ref={scroller}
            position="relative"
            bgcolor="background.paper"
            borderRadius={1}
            minHeight={0}
            overflowY="auto"
        >
            <List>
                {attempts.map(attempt => {
                    const isSelected = attempt.n === selected;
                    return (
                        <Fragment key={attempt.n}>
                            <ListItem
                                ref={isSelected ? selectedRow : undefined}
                                selected={isSelected}
                                divider={!isSelected}
                                cursor={isSelected ? 'default' : 'pointer'}
                                onClick={() => onSelect(attempt.n)}
                                label={startAndDuration(attempt)}
                                endAdornment={<JobStateLabel state={attempt.state} attempt />}
                            >
                                {intl.formatMessage(
                                    { id: 'job.attempt.tab', defaultMessage: 'Attempt {n}' },
                                    { n: attempt.n },
                                )}
                            </ListItem>
                            {isSelected && (
                                <Box
                                    ref={selectedFacts}
                                    bgcolor={selectedTint}
                                    px={2}
                                    pt={1}
                                    pb={2}
                                    borderBottom="1px solid"
                                    borderColor="divider"
                                >
                                    <AttemptSummary attempt={attempt} />
                                </Box>
                            )}
                        </Fragment>
                    );
                })}
            </List>
        </Box>
    );
}
